// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'

// HELPERS
import { usePrefixedContent } from '../../../../utils/content-helpers'
import { useMappedErrorContent } from '@/hooks'
import { checkIfUrlIsValid } from '@/utils'

// API
import { useProfileValidation } from '../../../../api/profile'

// COMPONENTS
import { BodyText, Checkbox, ContextualAlert, Row } from '@paypalcorp/pp-react'
import { WithLoadingShimmer } from '../../loading/with-loading-shimmer'

// TEST IDS
import { CONTACT_INFO_SECTION_TEST_ID } from '../../../../constants/test-ids'

// STYLES
import styles from './styles.module.less'

// TYPINGS
import { ContactInfoResponse } from '@/types'
import { InputValue } from '@/redux/features/create-profile-slice/types'

type ContactInfoSectionProps = {
  allContactInfo: ContactInfoResponse
  selectedContactInfoValues: ContactInfoResponse
  isValid: boolean
  updateSelectedContactInfo: (value: InputValue['value']) => void
  setIsValid: (isValid: InputValue['isValid']) => void
}

/**
 * Displays the selectable business contact info as checkboxes, where at least
 * one option must be selected
 */
const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({
  allContactInfo,
  selectedContactInfoValues,
  setIsValid,
  isValid,
  updateSelectedContactInfo,
}) => {
  // STATE
  const [validationErrors, setValidationErrors] = useState([])
  const [noSelectionError, setNoSelectionError] = useState('')
  const [invalidUrlError, setInvalidUrlError] = useState('')

  //  INITIALIZE CONTENT
  const getErrorContent = usePrefixedContent('hooks.error-handling.index.')
  const invalidUrlErrorMessage = getErrorContent('invalidUrlErrorMessage')
  const getContent = usePrefixedContent('business-info-sections.index.')
  const noSelectedContactInfoErrorMessageText = getContent(
    'noSelectedContactInfoErrorMessage',
  )
  const noContactInfoOnFileErrorMessageText = getContent(
    'noContactInfoOnFileErrorMessage',
  )
  const errorMessageContainerId = 'contactInfoErrorContainerId'

  // NETWORK REQUESTS
  const {
    data: validationResult,
    error: validationError,
    mutateAsync: validateProfileDetail,
    isPending: validationPending,
  } = useProfileValidation()

  const { mappedErrors } = useMappedErrorContent(
    validationResult,
    validationError,
    validationPending,
  )

  // CLICK HANDLERS
  function handleCheck(contactInfoKey: keyof ContactInfoResponse) {
    const isAlreadyChecked = selectedContactInfoValues[contactInfoKey]
    const newContactInfoSelections = {
      ...selectedContactInfoValues,
      [contactInfoKey]: isAlreadyChecked
        ? null
        : allContactInfo?.[contactInfoKey],
    }

    updateSelectedContactInfo(newContactInfoSelections)
  }

  // INITIALIZE CHECKBOXES
  const checkBoxOrder = [
    'businessPhoneNumber',
    'businessEmail',
    'businessWebsiteUrl',
  ]

  const contactInfoOptions: {
    contactInfoKey: keyof ContactInfoResponse
    value: string
  }[] =
    allContactInfo &&
    Object.keys(allContactInfo)
      ?.sort((a, b) => checkBoxOrder.indexOf(a) - checkBoxOrder.indexOf(b))
      ?.map((contactInfoKey: keyof ContactInfoResponse) => {
        return {
          contactInfoKey,
          value: selectedContactInfoValues[contactInfoKey] as string,
        }
      })

  const checkboxes = contactInfoOptions?.reduce(
    (displayedCheckboxes, { contactInfoKey, value }) => {
      const label = allContactInfo?.[contactInfoKey] as string

      if (label) {
        const isBusinessWebsiteUrl = contactInfoKey === 'businessWebsiteUrl'

        displayedCheckboxes.push(
          <Checkbox
            required={!!validationErrors[0] || !!noSelectionError}
            aria-describedby={errorMessageContainerId}
            key={label as string}
            name={`${contactInfoKey}Checkbox`}
            label={label as string}
            checked={!!value}
            error={
              isBusinessWebsiteUrl &&
              (!!invalidUrlError || !!validationErrors[0])
            }
            errorText={
              isBusinessWebsiteUrl && (invalidUrlError || validationErrors[0])
            }
            onChange={() => handleCheck(contactInfoKey)}
          />,
        )
      }

      return displayedCheckboxes
    },
    [],
  )

  useEffect(() => {
    const selectedValues = Object.values(selectedContactInfoValues)
    const everyCheckboxIsUnchecked = selectedValues.every((value) => !value)
    const checkedCheckboxes = selectedValues.filter((value) => !!value)

    if (
      selectedValues.length &&
      everyCheckboxIsUnchecked &&
      !noSelectionError
    ) {
      setNoSelectionError(noSelectedContactInfoErrorMessageText)
    } else if (
      ((selectedValues.length > 1 && checkedCheckboxes.length >= 1) ||
        (selectedValues.length === 1 && checkedCheckboxes.length === 1)) &&
      noSelectionError
    ) {
      setNoSelectionError('')
    }
  }, [
    selectedContactInfoValues,
    noSelectedContactInfoErrorMessageText,
    noSelectionError,
  ])

  useEffect(() => {
    const errorExists =
      !!validationErrors[0] ||
      !!invalidUrlError ||
      !!noSelectionError ||
      allContactInfo?.empty

    if (isValid && errorExists) {
      setIsValid(false)
    } else if (!isValid && !errorExists) {
      setIsValid(true)
    }
  }, [
    allContactInfo?.empty,
    validationErrors,
    invalidUrlError,
    noSelectionError,
    setIsValid,
    isValid,
  ])

  useEffect(() => {
    if (allContactInfo?.businessWebsiteUrl) {
      const urlIsValid = checkIfUrlIsValid(allContactInfo.businessWebsiteUrl)

      if (urlIsValid) {
        setInvalidUrlError('')
        validateProfileDetail({
          request: {
            businessWebsiteUrl: allContactInfo.businessWebsiteUrl,
          },
        })
      } else if (!invalidUrlError) {
        setInvalidUrlError(invalidUrlErrorMessage)
      }
    }
  }, [
    validateProfileDetail,
    allContactInfo?.businessWebsiteUrl,
    invalidUrlErrorMessage,
    invalidUrlError,
  ])

  useEffect(() => {
    setValidationErrors(mappedErrors)
  }, [mappedErrors])

  useEffect(() => {
    if (
      allContactInfo &&
      Object.keys(allContactInfo).length &&
      selectedContactInfoValues &&
      !Object.keys(selectedContactInfoValues).length
    ) {
      updateSelectedContactInfo(allContactInfo)
    }
  }, [allContactInfo, selectedContactInfoValues, updateSelectedContactInfo])

  return (
    <div
      data-testid={CONTACT_INFO_SECTION_TEST_ID}
      className={styles.contactInfoSectionContainer}
    >
      <BodyText strong className={styles.contactInfoLabel}>
        {getContent('businessContactInfoSectionLabel')}
      </BodyText>
      <WithLoadingShimmer
        isLoading={
          allContactInfo?.empty
            ? false
            : allContactInfo && !Object.keys(allContactInfo).length
        }
      >
        {allContactInfo?.empty ? (
          <ContextualAlert type='error'>
            {noContactInfoOnFileErrorMessageText}
          </ContextualAlert>
        ) : (
          <Row noGutters className={styles.checkboxContainer}>
            {!!noSelectionError && (
              <ContextualAlert
                type='error'
                className={styles.topErrorContainer}
              >
                {noSelectionError}
              </ContextualAlert>
            )}
            {checkboxes}
          </Row>
        )}
      </WithLoadingShimmer>
    </div>
  )
}

export default ContactInfoSection
